import { useEffect, useState } from "react";
import axios from "axios";

const KEY = "buzzerai_unlock_key";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LockGate — full-app maintenance wall. When the backend reports
 * `locked: true` on /api/lock/status, render the passcode screen instead
 * of the app. On successful unlock, stash the passcode in localStorage +
 * axios defaults so every subsequent request carries the X-App-Unlock
 * header through the server-side middleware.
 *
 * Server-side wall (server.py `app_lock_wall`) short-circuits every
 * /api/* except /api/lock/status + /api/unlock + /api/healthz when
 * APP_LOCKED=true. Twilio webhooks get a polite maintenance TwiML.
 */
export default function LockGate({ children }) {
  const [status, setStatus] = useState("checking");
  const [passcode, setPasscode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const check = async () => {
    try {
      const stored = localStorage.getItem(KEY);
      const r = await axios.get(`${API}/lock/status`);
      if (!r.data?.locked) { setStatus("unlocked"); return; }
      if (stored) {
        // Re-verify the stored key so a rotated passcode kicks the user
        // out immediately instead of failing silently on every request.
        try {
          const v = await axios.post(`${API}/unlock`, { passcode: stored });
          if (v.data?.ok) {
            axios.defaults.headers.common["X-App-Unlock"] = stored;
            setStatus("unlocked");
            return;
          }
        } catch (_) {
          localStorage.removeItem(KEY);
        }
      }
      setStatus("locked");
    } catch (_) {
      // If /lock/status itself is unreachable, fail SAFE (assume locked).
      setStatus("locked");
    }
  };

  useEffect(() => { check(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/unlock`, { passcode });
      if (r.data?.ok) {
        localStorage.setItem(KEY, r.data.token || passcode);
        axios.defaults.headers.common["X-App-Unlock"] = r.data.token || passcode;
        setStatus("unlocked");
      }
    } catch (_) {
      setErr("Wrong passcode. Try again.");
    }
    setBusy(false);
  };

  if (status === "checking") return null;
  if (status === "unlocked") return children;

  return (
    <div className="min-h-screen bg-[#0E1512] text-white flex items-center justify-center px-4" data-testid="lock-gate">
      <div className="w-full max-w-md text-center">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5C543] mb-3">BuzzerAI · locked</div>
        <h1 className="font-heading font-extrabold text-3xl mb-3 leading-tight">This app is temporarily paused.</h1>
        <p className="text-sm text-white/70 mb-8 leading-relaxed">
          BuzzerAI is under a private-beta pause. Enter the operator passcode to unlock.
        </p>
        <form onSubmit={submit} className="space-y-3" data-testid="lock-gate-form">
          <input
            type="password"
            autoFocus
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Operator passcode"
            className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:border-[#F5C543] transition-colors"
            data-testid="lock-gate-input"
          />
          {err && <div className="text-[#F5B5A0] text-xs" data-testid="lock-gate-error">{err}</div>}
          <button
            type="submit"
            disabled={busy || !passcode}
            className="w-full py-3 rounded-lg bg-[#F5C543] text-[#0E1512] font-heading font-extrabold hover:bg-[#F5D26A] transition-colors disabled:opacity-50"
            data-testid="lock-gate-submit"
          >
            {busy ? "Checking…" : "Unlock"}
          </button>
        </form>
        <div className="text-[10px] uppercase tracking-widest text-white/40 mt-8">
          Not you? This is a private beta pause.
        </div>
      </div>
    </div>
  );
}
